import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./Toolbar.scss";

import { makeStyles, Button } from "@fluentui/react-components";
import { ArrowExitFilled, DoorArrowLeft24Regular } from "@fluentui/react-icons";
import withConfig from "../config-validation/withConfig";
import ToolbarMic from "./FluentToolbarMicButton";
import ToolbarVideo from "./FluentToolbarVideoButton";
import ToolbarEmojisContainer from "./FluentToolbarEmojisContainer";
import ToolbarScreenShare from "./FluentToolbarShareScreenButton";
import ToolbarMoreButtonContainer from "./FluentToolbarMoreButtonContainer";
import { SocialButtonContainer } from "./FlexSocialButtonContainer";
import { ToolbarRaiseHandButtonContainer } from "./FluentToolbarRaiseHandButtonContainer";
import { usePeopleList } from "../room/usePeopleList";
import { SocialProvider } from "../contexts/SocialContext";
import { useModalContext } from "../room/ModalContext";
import { LeaveReason, LeaveRoomModal } from "../room/LeaveRoomModal";
import { FormattedMessage } from "react-intl";
import { useStateContext } from "../room/StateContext";
import { useCssBreakpoints } from "react-use-css-breakpoints";
import { FluentFileUpload } from "./FluentFileUpload";

const useStyles = makeStyles({
  buttonSplit: {
    display: "flex",
    width: "72px",
    height: "40px",
    maxHeight: "40px",
    alignItems: "flex-start",
    "& button": {
      height: "100%",
      backgroundColor: "#21242C",
      "& svg": {
        color: "#fff",
        fill: "#fff"
      }
    },
    "& button:first-child": {
      backgroundColor: "rgba(22, 24, 30, 0.60)",
      width: "40px",
      minWidth: "40px",
      "& span": {
        width: "24px",
        height: "24px",
        fontSize: "24px"
      }
    },
    "& button:last-child": {
      padding: "5px var(--spacingHorizontalM)",
      "& span": {
        width: "16px",
        height: "16px",
        fontSize: "16px"
      }
    }
  },
  buttonSplitDisabled: {
    "& button": {
      backgroundColor: "rgba(22, 24, 30, 0.60)",
      "&:hover": {
        backgroundColor: "rgba(22, 24, 30, 0.60)!important"
      }
    },
    "& svg": {
      color: "var(--colorNeutralForegroundDisabled, #F0F0F0)",
      fill: "var(--colorNeutralForegroundDisabled, #F0F0F0)"
    }
  },
  button: {
    height: "40px",
    minWidth: "40px",
    backgroundColor: "rgba(22, 24, 30, 0.60)",
    "& span": {
      width: "24px",
      height: "24px",
      fontSize: "24px"
    },
    "& svg": {
      color: "#fff",
      fill: "#fff"
    }
  },
  buttonDisabled: {
    backgroundColor: "rgba(22, 24, 30, 0.60)",
    "&:hover": {
      backgroundColor: "rgba(22, 24, 30, 0.60)"
    },
    "& svg": {
      color: "var(--colorNeutralForegroundDisabled, #F0F0F0)",
      fill: "var(--colorNeutralForegroundDisabled, #F0F0F0)"
    }
  },
  iconDisabled: {
    color: "var(--colorNeutralForegroundDisabled, #F0F0F0)!important",
    fill: "var(--colorNeutralForegroundDisabled, #F0F0F0)!important"
  },
  buttonActive: {
    backgroundColor: "#16181E",
    height: "40px",
    minWidth: "40px",
    "& span": {
      width: "24px",
      height: "24px",
      fontSize: "24px"
    },
    "& svg": {
      color: "#fff",
      fill: "#fff"
    }
  },
  buttonExit: {
    height: "40px",
    minWidth: "40px",
    backgroundColor: "#EB5757",
    "&:hover": {
      backgroundColor: "#ed6767"
    },
    "&:hover:active": {
      backgroundColor: "#ed6767"
    },
    "& span": {
      width: "24px",
      height: "24px",
      fontSize: "24px"
    },
    "& svg": {
      color: "#fff",
      fill: "#fff"
    }
  },
  buttonPressed: {
    backgroundColor: "#16181E"
  },
  menuListItem: {
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    },
    "&:hover > * ": {
      color: "inherit!important"
    }
  },
  micPopoverContainerVideo: {
    minWidth: "320px"
  },
  menuPopover: {
    top: "-15px!important"
  },
  micProgressContainer: {
    pointerEvents: "none",
    alignItems: "center",
    "&:hover": {},
    "&:hover > * ": {
      color: "inherit!important"
    }
  },
  progressBarColor: {
    "& .fui-ProgressBar__bar": {
      backgroundColor: "var(--Green-1, #219653)"
    }
  }
});

export function Toolbar({
  className,
  left,
  center,
  right,
  showToolbar = true,
  store,
  scene = window.APP?.scene,
  hubChannel,
  mySessionId,
  presences,
  hub,
  signedIn,
  objectFocused,
  ...rest
}) {
  const classes = useStyles();
  const people = usePeopleList(presences, mySessionId);
  const modalContext = useModalContext() || {};
  const { showNonHistoriedDialog } = modalContext;

  const stateContext = useStateContext() || {};
  const { entered, watching, onWatchingChange } = stateContext;

  const breakpoint = useCssBreakpoints();

  const isMobile = breakpoint === "sm" || breakpoint === "md";

  return (
    <div className={classNames(styles.toolbar, className, { hid: !showToolbar })}>
      <SocialProvider people={people} mySessionId={mySessionId}>
        {watching && (
          <Button
            appearance="subtle"
            icon={<DoorArrowLeft24Regular />}
            label={<FormattedMessage id="toolbar.join-room-button" defaultMessage="Go back" />}
            className={classes.button}
            onClick={() => onWatchingChange(false)}
          />
        )}
        {entered && (
          <>
            <Toolbar.Mic styles={classes} scene={scene} />
            {hubChannel.can("spawn_camera") && <Toolbar.Video styles={classes} scene={scene} hubChannel={hubChannel} />}
            {hubChannel.can("spawn_and_move_media") && (
              <Toolbar.ShareScreen styles={classes} scene={scene} hubChannel={hubChannel} />
            )}
            {hubChannel.can("spawn_emoji") && (
              <Toolbar.EmojisContainer styles={classes} scene={scene} hubChannel={hubChannel} />
            )}
            {!isMobile && hubChannel.can("spawn_emoji") && (
              <Toolbar.RaiseHandButtonContainer
                hubChannel={hubChannel}
                initialPresence={presences && presences[mySessionId]}
                store={store}
                styles={classes}
                scene={scene}
              />
            )}

            {/* TODO: Move this to the root file */}
            <FluentFileUpload />
          </>
        )}
        <Toolbar.SocialButtonContainer
          styles={classes}
          scene={scene}
          hubChannel={hubChannel}
          mySessionId={mySessionId}
          presences={presences}
        />
        {/* <Button
        appearance="subtle"
        className={classes.button}
        icon={<Grid24Regular style={{ width: "24px", height: "24px" }} />}
      /> */}
        <Toolbar.MoreButton
          styles={classes}
          store={store}
          scene={scene}
          hubChannel={hubChannel}
          hub={hub}
          signedIn={signedIn}
          mySessionId={mySessionId}
          presences={presences}
          initialPresence={presences && presences[mySessionId]}
          {...rest}
        />
        <Button
          appearance="subtle"
          className={classes.buttonExit}
          icon={<ArrowExitFilled style={{ width: "24px", height: "24px" }} />}
          onClick={() =>
            showNonHistoriedDialog(LeaveRoomModal, {
              reason: LeaveReason.leaveRoom,
              onLeave: () => {
                scene.emit("leave_room_requested");
              }
            })
          }
        />
      </SocialProvider>
    </div>
  );
}

Toolbar.propTypes = {
  className: PropTypes.string,
  left: PropTypes.node,
  center: PropTypes.node,
  right: PropTypes.node,
  hideLeft: PropTypes.string,
  hideRight: PropTypes.string,
  showToolbar: PropTypes.bool,
  scene: PropTypes.object,
  hubChannel: PropTypes.object,
  store: PropTypes.object,
  mySessionId: PropTypes.string,
  presences: PropTypes.object,
  hub: PropTypes.object,
  signedIn: PropTypes.bool,
  objectFocused: PropTypes.bool
};

Toolbar.Mic = withConfig(ToolbarMic, `voiceChat.enabled`);

Toolbar.Video = withConfig(ToolbarVideo, `spawnCamera.enabled`);

Toolbar.ShareScreen = withConfig(ToolbarScreenShare, `spawnAndMoveMedia.enabled`);

Toolbar.EmojisContainer = withConfig(ToolbarEmojisContainer, `spawnEmoji.enabled`);

Toolbar.RaiseHandButtonContainer = withConfig(ToolbarRaiseHandButtonContainer, `spawnEmoji.enabled`);

Toolbar.MoreButton = withConfig(ToolbarMoreButtonContainer, `toolbar.moreButton.enabled`);

Toolbar.SocialButtonContainer = SocialButtonContainer;
